/**
 * Marquee (s7gdy9l8) snippet-author runtime.
 *
 * Drives the marquee animation duration and pause gestures for each
 * `[data-spectrum-instance-id][data-spectrum-snippet-id="s7gdy9l8"]`
 * container on the page.
 *
 * Animation itself is CSS — `@keyframes sai-s7gdy9l8-marquee` translates the
 * track from 0 to -50% over `--sai-s7gdy9l8-duration`. The browser compositor
 * runs the animation; this module only sets the duration value (so pixel
 * speed stays constant regardless of total slide width) and pauses on
 * gesture / focus / reduced-motion.
 *
 * Positioning is intentionally not a snippet concern. The host renders in
 * normal flow; whichever section / template places the bar decides whether
 * to wrap it in a sticky / fixed container.
 *
 * applyVariant updates DOM attributes and classes for bar-level scalars on
 * every variant resolution. Downstream effects vary:
 *
 *   - `speed_seconds`      → updates `data-ticker-seconds`; setDuration()
 *                            re-reads the attribute on each invocation, and
 *                            the bind callback calls it after applyVariant
 *                            so the marquee speed picks up the new value.
 *   - `pause_on_hover`     → updates the `data-pause-on-hover` attribute,
 *                            so CSS hover-pause responds live. The focus /
 *                            touch listeners however are wired once at init
 *                            based on the initial value — flipping this at
 *                            runtime won't add or remove those listeners.
 *
 * Item content (the inline-list) is server-rendered and not mutable here —
 * a variant rule that changes item text or logos requires a fresh page
 * render.
 *
 * Test surface: when `globalThis.__SAI_TEST_HARNESS__ === true`, exposes
 * `globalThis.__saiS7gdy9l8` with `{ applyVariant, computeDuration }` for
 * unit tests.
 */
;(() => {
  const SNIPPET_ID = 's7gdy9l8'
  const ROOT_SELECTOR = `.sai-${SNIPPET_ID}`
  const TRACK_SELECTOR = `.sai-${SNIPPET_ID}__track`
  const VIEWPORT_SELECTOR = `.sai-${SNIPPET_ID}__viewport`
  const COPY_SELECTOR = `.sai-${SNIPPET_ID}__track-copy`

  /**
   * Compute the marquee animation duration. `tickerSeconds` is "seconds for
   * one viewport-width of slide content to scroll past." The animation
   * actually moves the track by one full copy's width (translate -50% of
   * the 2-copy track), so duration scales with content length to keep
   * pixels-per-second constant regardless of slide count.
   */
  function computeDuration(tickerSeconds, copyWidth, viewportWidth) {
    if (!Number.isFinite(tickerSeconds) || tickerSeconds <= 0) return null
    if (!Number.isFinite(copyWidth) || copyWidth <= 0) return null
    if (!Number.isFinite(viewportWidth) || viewportWidth <= 0) return null
    return tickerSeconds * (copyWidth / viewportWidth)
  }

  function readBoolAttr(node, name, fallback) {
    const value = node.getAttribute(name)
    if (value === 'true') return true
    if (value === 'false') return false
    return fallback
  }

  function readNumberAttr(node, name, fallback) {
    const raw = node.getAttribute(name)
    if (raw === null || raw === '') return fallback
    const parsed = Number(raw)
    return Number.isFinite(parsed) ? parsed : fallback
  }

  function applyVariant(node, content) {
    const root = node.querySelector(ROOT_SELECTOR)
    const track = node.querySelector(TRACK_SELECTOR)
    if (!root || !track) return

    if (typeof content.speed_seconds === 'number') {
      root.setAttribute('data-ticker-seconds', String(content.speed_seconds))
    }

    if (typeof content.pause_on_hover === 'boolean') {
      const v = content.pause_on_hover ? 'true' : 'false'
      root.setAttribute('data-pause-on-hover', v)
      track.setAttribute('data-pause-on-hover', v)
    }
  }

  if (globalThis.__SAI_TEST_HARNESS__ === true) {
    globalThis.__saiS7gdy9l8 = {
      applyVariant,
      computeDuration,
      readBoolAttr,
      readNumberAttr,
    }
  }

  /**
   * Set up one container's runtime: animation duration,
   * pause-on-focus/touch, click and view analytics. Returns a teardown
   * function.
   */
  function activateContainer(node) {
    const root = node.querySelector(ROOT_SELECTOR)
    const track = node.querySelector(TRACK_SELECTOR)
    const viewport = node.querySelector(VIEWPORT_SELECTOR)
    const firstCopy = node.querySelector(COPY_SELECTOR)
    if (!root || !track || !viewport || !firstCopy) return () => {}

    // pauseOnHover is captured once at init — flipping it via applyVariant
    // updates the data attribute (CSS hover-pause responds live) but does
    // not add or remove the focus / touch listeners.
    const pauseOnHover = readBoolAttr(root, 'data-pause-on-hover', true)

    const reducedMotion =
      typeof window.matchMedia === 'function'
        ? window.matchMedia('(prefers-reduced-motion: reduce)')
        : null

    // When the natural slide content is shorter than the viewport, the
    // duplicate-track marquee would scroll empty space between cycles.
    // Clone the snapshot of original children additively (1× per iteration)
    // until the copy is at least as wide as the viewport. Snapshotting
    // ONCE up-front matters: re-reading copy.children each iteration grows
    // the clone count exponentially (2, 4, 8 …) and a single zero-width
    // child would lock the page at 2^32 clones before the safety cap.
    function fillCopiesToViewport() {
      const viewportWidth = viewport.getBoundingClientRect().width
      if (!viewportWidth) return
      const copies = node.querySelectorAll(COPY_SELECTOR)
      for (const copy of copies) {
        const originals = Array.from(copy.children)
        if (originals.length === 0) continue
        let safety = 32
        while (copy.getBoundingClientRect().width < viewportWidth && safety-- > 0) {
          const widthBefore = copy.getBoundingClientRect().width
          for (const child of originals) {
            copy.appendChild(child.cloneNode(true))
          }
          // If a full pass over the original children didn't widen the
          // copy at all, the children render at zero width — bail rather
          // than burn the remaining safety budget on no-op appends.
          if (copy.getBoundingClientRect().width <= widthBefore) break
        }
      }
    }
    fillCopiesToViewport()

    function setDuration() {
      if (reducedMotion?.matches) return
      // Re-read each call so applyVariant's data-ticker-seconds update is
      // picked up without needing to thread state.
      const tickerSeconds = readNumberAttr(root, 'data-ticker-seconds', 30)
      const copyWidth = firstCopy.getBoundingClientRect().width
      const viewportWidth = viewport.getBoundingClientRect().width
      const duration = computeDuration(tickerSeconds, copyWidth, viewportWidth)
      if (duration === null) return
      track.style.setProperty(`--sai-${SNIPPET_ID}-duration`, `${duration}s`)
    }

    // On viewport widen (browser resize, mobile rotate, devtools toggle), an
    // earlier copy that was wide enough may now expose blank space at -50%.
    // Re-fill before recomputing duration so the marquee never animates over
    // an under-wide track.
    function onResize() {
      fillCopiesToViewport()
      setDuration()
    }
    setDuration()

    // Flip the host's data-ready attribute so CSS unpauses the animation.
    // This must happen AFTER setDuration so the marquee starts at the
    // correct speed; otherwise it would briefly run at the default duration
    // and snap-restart when JS updates the value.
    root.setAttribute('data-ready', 'true')

    let resizeObserver = null
    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(onResize)
      resizeObserver.observe(firstCopy)
      resizeObserver.observe(viewport)
    } else {
      window.addEventListener('resize', onResize)
    }

    const onMotionChange = () => {
      if (reducedMotion?.matches) {
        track.style.setProperty(`--sai-${SNIPPET_ID}-duration`, '0s')
      } else {
        setDuration()
      }
    }
    if (reducedMotion && typeof reducedMotion.addEventListener === 'function') {
      reducedMotion.addEventListener('change', onMotionChange)
    }
    onMotionChange()

    let getTrack = null
    function fireAnalytics(eventName, payload) {
      if (!getTrack) return
      try {
        getTrack(eventName, payload)
      } catch {
        // Analytics is best-effort — never let a tracking error break the bar.
      }
    }

    function pause(reason) {
      track.style.animationPlayState = 'paused'
      fireAnalytics('marquee:pause', { reason })
    }
    function resume(reason) {
      // Clear the inline property rather than setting 'running'. The CSS
      // `:hover` rule has no `!important`, so an inline `running` would
      // permanently outrank it — once a keyboard or touch interaction
      // routed through resume(), hover-pause would stop working for the
      // rest of the page lifetime. Removing the inline property hands
      // control back to CSS (which handles both hover-pause and the
      // data-ready running state).
      track.style.removeProperty('animation-play-state')
      fireAnalytics('marquee:resume', { reason })
    }

    const focusListeners = []
    if (pauseOnHover) {
      const onFocusIn = () => pause('focus')
      const onFocusOut = () => resume('focus_end')
      const onTouchStart = () => pause('touch')
      const onTouchEnd = () => resume('touch_end')
      node.addEventListener('focusin', onFocusIn)
      node.addEventListener('focusout', onFocusOut)
      node.addEventListener('touchstart', onTouchStart, { passive: true })
      node.addEventListener('touchend', onTouchEnd, { passive: true })
      focusListeners.push(
        ['focusin', onFocusIn],
        ['focusout', onFocusOut],
        ['touchstart', onTouchStart],
        ['touchend', onTouchEnd],
      )
    }

    // Click analytics — delegate on the container so newly-rendered slides
    // (after applyVariant) are covered.
    function onClick(event) {
      const target = event.target instanceof Element ? event.target : null
      if (!target) return
      const slideEl = target.closest(`.sai-${SNIPPET_ID}__slide`)
      if (!slideEl || !node.contains(slideEl)) return
      const idx = Number(slideEl.getAttribute('data-slide-index') || '0')
      const href = slideEl.getAttribute('href') || ''
      fireAnalytics('marquee:item_click', {
        item_index: idx,
        link_url: href,
      })
    }
    node.addEventListener('click', onClick)

    // IntersectionObserver — emit slide_view exactly once per slide on the
    // first time it crosses 50% visibility. We observe the FIRST copy only;
    // the duplicated copy is decorative.
    let intersectionObserver = null
    const reportedViews = new Set()
    if (typeof IntersectionObserver !== 'undefined') {
      intersectionObserver = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (!entry.isIntersecting || entry.intersectionRatio < 0.5) continue
            const idx = entry.target.getAttribute('data-slide-index')
            if (idx === null || reportedViews.has(idx)) continue
            reportedViews.add(idx)
            fireAnalytics('marquee:item_view', {
              item_index: Number(idx),
            })
          }
        },
        { threshold: [0, 0.5, 1], root: viewport },
      )
      const slidesToObserve = firstCopy.querySelectorAll(`.sai-${SNIPPET_ID}__slide`)
      for (const s of slidesToObserve) intersectionObserver.observe(s)
    }

    function teardown() {
      if (resizeObserver) resizeObserver.disconnect()
      else window.removeEventListener('resize', onResize)
      if (reducedMotion && typeof reducedMotion.removeEventListener === 'function') {
        reducedMotion.removeEventListener('change', onMotionChange)
      }
      for (const [evt, fn] of focusListeners) node.removeEventListener(evt, fn)
      node.removeEventListener('click', onClick)
      if (intersectionObserver) intersectionObserver.disconnect()
    }

    return {
      teardown,
      setTrackHandle: (track) => {
        getTrack = track
      },
      // Re-compute marquee duration. Called by the bind callback after
      // applyVariant updates `data-ticker-seconds` so the new speed takes
      // effect without a page reload.
      refreshDuration: setDuration,
    }
  }

  function activate(node, snippetApi) {
    const handle = activateContainer(node)
    if (!handle || typeof handle.teardown !== 'function') return

    // Wire to the SDK if available; otherwise the bar runs without analytics
    // and without runtime variant resolution (SSR is enough for both).
    if (snippetApi && typeof snippetApi.bind === 'function') {
      const bound = snippetApi.bind(node, ({ variants, currentVariantId }) => {
        const variant = variants.find((v) => v.variantId === currentVariantId)
        if (!variant || !variant.content) return
        applyVariant(node, variant.content)
        // applyVariant only updates the data attribute; setDuration reads
        // it live, so re-running here lets `ticker_seconds` changes take
        // effect on variant switch without a page reload.
        handle.refreshDuration()
      })
      if (bound && typeof bound.track === 'function') {
        handle.setTrackHandle(bound.track)
      }
    }
  }

  function init() {
    const containers = document.querySelectorAll(
      `[data-spectrum-instance-id][data-spectrum-snippet-id="${SNIPPET_ID}"]`,
    )
    const snippetApi = window.__spectrumAi?.snippet

    // Per snippet-library convention, defer setup until the wrapper's
    // vis-gate flips to "on". For SSR-visible bars this is immediate; for
    // bars hidden by the SDK pending variant resolution, we wait for the
    // SDK to remove the `off` value rather than running animation /
    // observer setup against a `display:none` container.
    for (const node of containers) {
      if (node.getAttribute('data-spectrum-vis') === 'off') {
        const observer = new MutationObserver((records) => {
          for (const record of records) {
            if (record.attributeName !== 'data-spectrum-vis') continue
            if (node.getAttribute('data-spectrum-vis') === 'off') continue
            observer.disconnect()
            activate(node, snippetApi)
            return
          }
        })
        observer.observe(node, { attributes: true, attributeFilter: ['data-spectrum-vis'] })
        continue
      }
      activate(node, snippetApi)
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true })
  } else {
    init()
  }
})()
